import { useEffect, useRef, useState } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { type ModalFC } from '@/components/modals/modalsManager';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trackEvent } from '@/utils/analytics';

import { ReferTab } from './refer-tab';
import { useReferralCodeQuery, useReferralStatsQuery } from './referral.query';
import { StatusTab } from './status-tab';

export const ReferralModal: ModalFC<{}> = ({ open, onOpenChange }) => {
  const { data: code, isLoading: isCodeLoading } = useReferralCodeQuery();
  const { data: stats, isLoading: isStatsLoading } = useReferralStatsQuery();

  const referralCount = stats?.referred_users?.length ?? 0;
  const isActivity = code?.reward_type === 'activity_based';
  const hasTrackedViewRef = useRef(false);
  const [activeTab, setActiveTab] = useState('refer');

  useEffect(() => {
    if (!open) {
      hasTrackedViewRef.current = false;
      setActiveTab('refer');
      return;
    }
    if (hasTrackedViewRef.current || !code) return;
    hasTrackedViewRef.current = true;
    trackEvent('REFERRAL_PAGE_VIEWED', {
      experiment_variant: code.reward_type ?? 'payment_based',
    });
  }, [open, code]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        showCloseButton={false}
        className="grid h-[42rem] max-h-[85vh] max-w-xl w-full grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden border-0 bg-background p-0"
      >
        <Dialog.Title className="sr-only">
          {isActivity ? 'Get 1 month free' : 'Get 2 months free'}
        </Dialog.Title>
        <Dialog.Description className="sr-only">
          Refer friends and earn free Plus months
        </Dialog.Description>

        <RewardBanner isActivity={isActivity} />

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          size="lg"
          className="min-h-0 w-full items-stretch gap-4 px-6 pb-6 pt-5"
        >
          <XStack className="w-full items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="refer">Refer</TabsTrigger>
              <TabsTrigger value="status">
                {referralCount > 0 ? `Status (${referralCount})` : 'Status'}
              </TabsTrigger>
            </TabsList>
            {activeTab === 'refer' && (
              <HowItWorksSubtext isActivity={isActivity} />
            )}
          </XStack>

          <TabsContent
            value="refer"
            className="mt-0 min-h-0 w-full overflow-y-auto scrollbar-hide"
          >
            <ReferTab
              code={code}
              isLoading={isCodeLoading}
              onClose={() => onOpenChange(false)}
            />
          </TabsContent>

          <TabsContent
            value="status"
            className="mt-0 min-h-0 w-full overflow-y-auto scrollbar-hide"
          >
            <StatusTab stats={stats} isLoading={isStatsLoading} />
          </TabsContent>
        </Tabs>
      </Dialog.Content>
    </Dialog.Root>
  );
};

function RewardBanner({ isActivity }: { isActivity: boolean }) {
  const headline = isActivity ? 'One month free' : 'Two months free';

  return (
    <XStack className="relative w-full items-center justify-between gap-4 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 px-6 py-7">
      <YStack className="min-w-0 gap-3">
        <YStack className="gap-1">
          <p className="font-serif text-3xl leading-tight tracking-tight text-white">
            {headline}
          </p>
          <p className="text-sm text-white/60">
            For you and everyone you refer
          </p>
        </YStack>
        <XStack className="w-fit items-center gap-2 rounded-full bg-black/30 px-3 py-1.5">
          <Icons.sparkle className="size-3.5 text-amber-400" />
          <span className="text-sm font-medium text-white/70">
            Unlimited referrals
          </span>
        </XStack>
      </YStack>

      <XStack
        center
        className="size-16 shrink-0 rounded-full bg-white/10 backdrop-blur-sm"
      >
        <AppLogo className="size-9" customColor="white" />
      </XStack>

      <Dialog.Close className="absolute right-3 top-3 bg-transparent p-1.5 text-white hover:bg-white/10 hover:text-white" />
    </XStack>
  );
}

function HowItWorksSubtext({ isActivity }: { isActivity: boolean }) {
  const text = isActivity
    ? 'They use LittleBird for 3 days · you both get 1 month free'
    : 'They subscribe · you both get 2 months of Plus';

  return (
    <p className="max-w-[14rem] text-right text-xs leading-snug text-foreground-muted sm:max-w-[18rem]">
      {text}
    </p>
  );
}
